import React, { useState, useEffect, useCallback, useRef } from 'react';
// --------------------------------------------------------------------------------
import { String, StringProps } from './String';
import styled from 'styled-components';
import { config } from '../index';
import { ActionIcon } from './ActionIcon';

// --------------------------------------------------------------------------------
// 📌  reference to repo for more information
// https://github.com/WoobieTheBear/react-date-picker
// --------------------------------------------------------------------------------

export interface RangeDatePickerProps {
  /* RangeDatePicker border radius */
  borderRadius?: string;
  /* RangeDatePicker label */
  label?: StringProps;
  /* RangeDatePicker name */
  name?: string;
  /* RangeDatePicker onChange callback */
  onChange?: (param?: any) => void;
}

interface InnerProps extends RangeDatePickerProps {
  /* RangeDatePicker button state */
  disabled?: boolean;
  isPast?: boolean;
  bgColor?: string;
  color?: string;
}

interface FormProps {
  visible?: boolean;
  selectedDayFrom?: Date;
  selectedDayTo?: Date;
  month?: number;
  nextMonth?: number;
  year?: number;
  nextYear?: number;
  monthDetails?: any;
  monthPlusDetails?: any;
  update?: boolean;
}

const RangeDatePickerContainer = styled.div<RangeDatePickerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  user-select: none;
`;

const TodayTimestampContainer = styled.div<RangeDatePickerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 0 ${config.spacingSmall} 0;
  margin: 0 0 ${config.spacingSmall} 0;
  border-bottom: 1px solid ${config.muted};
`;

const NavigationContainer = styled.div<RangeDatePickerProps>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
`;

const PickerWrapper = styled.div<RangeDatePickerProps>`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  padding: 16px 12px;
  border: 1px solid ${config.silver};
  border-radius: ${config.spacingXLarge};
  box-shadow: ${config.boxShadow};
`;

const StyledInput = styled.div<RangeDatePickerProps>`
  width: 100%;
  padding: ${config.spacingSmall} ${config.fontXMedium};
  border: 1px solid ${config.silver};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${config.white};
  color: ${config.primary};
  font-size: ${config.fontMedium};
  user-select: none;
  cursor: pointer;

  &::after {
    content: '🗓';
    position: absolute;
    right: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const CalendarContainer = styled.div<RangeDatePickerProps>`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${config.spacingXXXXLarge};
`;

const ContentWrapper = styled.div<RangeDatePickerProps>`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const WeekDayWrapper = styled.div<RangeDatePickerProps>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 10px;
  text-align: center;
  font-size: ${config.fontSmall};
  color: ${config.grey};
  text-transform: capitalize;
  margin: ${config.spacingMedium} 0;
`;

const DayPickerWrapper = styled.div<RangeDatePickerProps>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 10px;
  margin-top: 10px;
  color: ${config.grey};
`;

const StyledButton = styled.div<InnerProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-size: ${config.fontMedium};
  padding: 8px;
  transition: ${config.transitionShort};
  border-radius: 50%;
  color: ${(props) => props.color};
  background-color: ${(props) => props.bgColor};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    color: ${(props) =>
      props.disabled || props.isPast ? config.secondary : config.white};
    background-color: ${(props) =>
      props.disabled || props.isPast ? config.silver : config.primary};
      opacity: ${(props) => (props.isPast ? 0.5 : 1)}};
  }
`;

export const RangeDatePicker: React.FC<RangeDatePickerProps> = ({
  onChange,
  borderRadius,
}) => {
  const oneDay = 60 * 60 * 24 * 1000;
  const timeZoneOffsetInMinutes = -new Date().getTimezoneOffset();
  const todayTimestamp = Date.now() - (Date.now() % oneDay);
  // 👉 get date today in dd month yyyy format
  const today = new Date(todayTimestamp).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  // 👉 convert selected date in to day month year format
  const datePrettifier = (date: Date) => {
    if (!date) return '';

    return date?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const [form, setForm] = useState<FormProps>({
    visible: true,
    month: new Date(todayTimestamp).getMonth(),
    nextMonth: new Date(todayTimestamp).getMonth() + 1,
    year: new Date(todayTimestamp).getFullYear(),
    // 👉 get next month from current month and year based on month
    nextYear: new Date(
      new Date(todayTimestamp).getFullYear(),
      new Date(todayTimestamp).getMonth() + 1,
      0
    ).getFullYear(),
  });
  // get total number of days from selectedDayFrom to selectedDayTo
  const getDaysBetweenDates = (date1: Date, date2: Date) => {
    if (!date1 || !date2) return 0;

    const diffInMs = Math.abs(date2.valueOf() - date1.valueOf());
    return diffInMs / oneDay + 1; // add 1 to include the last day
  };

  const dayFromRef = useRef<any>(undefined); // TODO fix ref type
  const dayToRef = useRef<any>(undefined); // TODO fix ref type
  console.log('⭐️ from ', form?.selectedDayFrom, '⭐️');
  console.log('⭐️ to ', form?.selectedDayTo, '⭐️');

  const MONTH_ARRAY = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const getMonthStr = (month: number) =>
    MONTH_ARRAY[Math.max(Math.min(11, month), 0)] || 'Month';

  const equalDate = (leftHand: Date, rightHand: Date) => {
    return (
      leftHand.getFullYear() === rightHand.getFullYear() &&
      leftHand.getMonth() === rightHand.getMonth() &&
      leftHand.getDate() === rightHand.getDate()
    );
  };

  const getMonthDetails = useCallback(
    (year: number, month: number) => {
      // this months data
      const firstDay = new Date(year, month).getDay();
      const numberOfDays = 40 - new Date(year, month, 40).getDate();
      const numberOfFields = Math.ceil((firstDay + numberOfDays) / 7) * 7;

      // next months data
      const { _year: prevYear, _month: prevMonth } = countMonthDown(
        year,
        month
      );
      const numberOfDaysPrevMonth =
        40 - new Date(prevYear, prevMonth, 40).getDate();

      const monthArray = [...Array.from(Array(numberOfFields).keys())].map(
        (index) => {
          const delta = index - firstDay;
          let dayOfMonth = 0;
          let appearance = 'invalid';
          let dateObject = null;

          if (delta < 0) {
            // day is from previous month
            dayOfMonth = numberOfDaysPrevMonth + delta + 1;
            const { _year, _month } = countMonthDown(year, month);
            dateObject = new Date(_year, _month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);
          } else if (delta >= numberOfDays) {
            // day is from next month
            dayOfMonth = delta - numberOfDays + 1;
            const { _year, _month } = countMonthUp(year, month);
            dateObject = new Date(_year, _month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);
          } else {
            // day is from this month
            dayOfMonth = delta + 1;
            dateObject = new Date(year, month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);

            appearance =
              form?.selectedDayFrom &&
              equalDate(form?.selectedDayFrom, dateObject)
                ? 'selected'
                : dateObject.valueOf() === todayTimestamp
                ? 'current'
                : 'valid';
          }
          // NOTE: these dateObjects have timezone offset
          return {
            appearance,
            dateObject,
          };
        }
      );

      return monthArray;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [todayTimestamp, form?.selectedDayFrom]
  );

  const updateDetails = useCallback(() => {
    const details = getMonthDetails(
      form?.year as number,
      form?.month as number
    ); // 👉 get selected month details
    const monthPlusDetails = getMonthDetails(
      form?.year as number,
      (form?.month as number) + 1
    ); // 👉 get next month details

    setForm({ ...form, monthDetails: details, monthPlusDetails });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMonthDetails, form?.month, form?.year]);

  useEffect(() => {
    if (!form?.update) {
      updateDetails();
      return () => {
        setForm({ ...form, update: true });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.update, updateDetails]);

  const countMonthUp = (year: number, month: number) => {
    let _year = year;
    let _month = month + 1;
    if (_month > 11) {
      _month = 0;
      _year++;
    }
    return { _year, _month };
  };

  const countMonthDown = (year: number, month: number) => {
    let _year = year;
    let _month = month - 1;
    if (_month < 0) {
      _month = 11;
      _year--;
    }
    return { _year, _month };
  };

  const selectDay = ({ dateObject }: { dateObject: Date }) => {
    // ⚠️ don't allow to select days in the past
    if (dateObject.valueOf() < todayTimestamp) {
      return;
    }

    const dayFrom = form?.selectedDayFrom;
    const dayTo = form?.selectedDayTo;

    // ⚠️ don't dayTo to be less than day from
    if (dayFrom && dateObject.valueOf() < dayFrom.valueOf()) {
      return;
    }

    // --------------------------------------------------------------------------------
    // ⚠️ update selected day from and to based on from values
    // --------------------------------------------------------------------------------
    if (dayFrom && dayTo) {
      dayFromRef.current = dateObject;
      dayToRef.current = undefined;
    } else if (dayFrom) {
      dayToRef.current = dateObject;
    } else {
      dayFromRef.current = dateObject;
    }
    const totalDays = getDaysBetweenDates(dayFromRef.current, dayToRef.current);

    // return;
    setForm({
      ...form,
      selectedDayFrom: dayFromRef.current, // 👉 set selected day from
      selectedDayTo: dayToRef.current, // 👉 set selected day to
      // visible: false,
      update: false,
    });

    if (dayFromRef.current && dayToRef.current)
      // --------------------------------------------------------------------------------
      // 👉 call onChange function with selected dates
      // --------------------------------------------------------------------------------
      onChange?.({
        dayFrom: dayFromRef.current,
        dayTo: dayToRef.current,
        totalDays,
      });
  };

  const selectYear = (year: number, month: number, offset: number) => {
    let updatedYear = year + offset;
    setForm({
      ...form,
      year: updatedYear,
      monthDetails: getMonthDetails(updatedYear, month),
    });
  };

  const selectMonth = (year: number, month: number, offset: number) => {
    const { _year, _month } =
      offset > 0 ? countMonthUp(year, month) : countMonthDown(year, month);

    const upMonth = countMonthUp(_year, _month); // 👉 calculate +1 month details

    setForm({
      ...form,
      year: _year,
      month: _month,
      monthDetails: getMonthDetails(_year, _month),
      nextMonth: upMonth?._month,
      nextYear: upMonth?._year,
      monthPlusDetails: getMonthDetails(upMonth?._month, upMonth?._year),
    });
  };

  const showRangeDatePicker = () => {
    setForm({ ...form, visible: !form?.visible });
  };

  const inputOnKey = (event: { code: string; preventDefault: () => void }) => {
    if (event.code === 'Enter') {
      event.preventDefault();
      let dateValue = new Date(dayFromRef.current);

      if (dateValue.valueOf() > 0) {
        setForm({
          ...form,
          selectedDayFrom: dateValue,
          year: dateValue.getFullYear(),
          month: dateValue.getMonth(),
          visible: false,
          update: false,
        });

        if (onChange) {
          onChange(dateValue.valueOf());
        }
      } else {
        onChange?.(-1);
      }
    }
    if (event.code === 'Escape') {
      setForm({ ...form, visible: false });
    }
  };

  const ServeCalendar = ({ month }: { month: any }) => {
    return (
      <ContentWrapper>
        <WeekDayWrapper>
          {['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'].map((day, index) => (
            <span key={index}>{day}</span>
          ))}
        </WeekDayWrapper>

        <DayPickerWrapper>
          {month?.map(
            (entry: { appearance?: any; dateObject: any }, key: number) => {
              const disabled = entry?.appearance === 'invalid';
              const isPast = entry.dateObject.valueOf() < todayTimestamp; // 👉 check if date object is in the past
              const isDayFrom =
                entry.dateObject.valueOf() === form?.selectedDayFrom?.valueOf(); // 👉 check if date object is selected day from
              const isDayTo =
                entry.dateObject.valueOf() === form?.selectedDayTo?.valueOf(); // 👉 check if date object is selected day to
              let isRangeDay; // 👉 check if date object is in range between day from and day to

              if (form?.selectedDayFrom && form?.selectedDayTo)
                isRangeDay =
                  entry.dateObject.valueOf() >
                    form?.selectedDayFrom?.valueOf() &&
                  entry.dateObject.valueOf() < form?.selectedDayTo?.valueOf();

              // --------------------------------------------------------------------------------
              // 📌  determine UI appearance for day components
              // --------------------------------------------------------------------------------
              let bgColor = config.silver;
              let color = config.secondary;
              if ((isDayFrom || isDayTo) && !disabled) {
                bgColor = config.darkMuted;
                color = config.white;
              }
              if (isRangeDay && !disabled) {
                bgColor = config.transparentMat;
              }

              return (
                <StyledButton
                  key={key}
                  isPast={isPast}
                  disabled={disabled}
                  bgColor={bgColor}
                  color={color}
                  onMouseDown={() => selectDay(entry)}
                >
                  {entry.dateObject.getDate()}
                </StyledButton>
              );
            }
          )}
        </DayPickerWrapper>
      </ContentWrapper>
    );
  };

  return (
    <div className="container">
      <RangeDatePickerContainer className="RangeDatePicker0">
        <StyledInput borderRadius={borderRadius} onClick={showRangeDatePicker}>
          {form?.selectedDayFrom &&
            `${datePrettifier(
              form?.selectedDayFrom as Date
            )} to ${datePrettifier(form?.selectedDayTo as Date)} `}

          {!form?.selectedDayFrom && 'Add date'}
        </StyledInput>
        <input
          type="datetime-local"
          ref={dayFromRef}
          onKeyDown={inputOnKey}
          style={{ display: 'none' }} // 👉 hide the in UI
        />

        {form?.visible && (
          <PickerWrapper>
            <TodayTimestampContainer>
              <String
                string={today}
                align="center"
                fontSize={config.fontXMedium}
                color={config.secondary}
              />
            </TodayTimestampContainer>
            <NavigationContainer>
              <ActionIcon
                icon="chevron-left"
                border={`1px solid ${config.transparent}`}
                fill={config.secondary}
                hoverFill={config.silver}
                onClick={() =>
                  selectYear(form?.year as number, form?.month as number, -1)
                }
                display={false}
              />
              <ActionIcon
                icon="arrow-left"
                border={`1px solid ${config.primary}`}
                fill={config.secondary}
                hoverFill={config.silver}
                size={12}
                onClick={() =>
                  selectMonth(form?.year as number, form?.month as number, -1)
                }
              />{' '}
              <CalendarContainer>
                <ContentWrapper>
                  <String
                    string={form?.year + ''}
                    align="center"
                    fontSize={config.fontMedium}
                    color={config.secondary}
                  />
                  <String
                    string={getMonthStr(form?.month as number)}
                    align="center"
                    fontSize={config.fontXMedium}
                    color={config.secondary}
                  />
                </ContentWrapper>
                <ContentWrapper>
                  <String
                    string={form?.nextYear + ''}
                    align="center"
                    fontSize={config.fontMedium}
                    color={config.secondary}
                  />
                  <String
                    string={getMonthStr(form?.nextMonth as number)}
                    align="center"
                    fontSize={config.fontXMedium}
                    color={config.secondary}
                  />
                </ContentWrapper>
              </CalendarContainer>
              <ActionIcon
                icon="arrow-right"
                border={`1px solid ${config.primary}`}
                fill={config.secondary}
                hoverFill={config.silver}
                size={12}
                onClick={() =>
                  selectMonth(form?.year as number, form?.month as number, 1)
                }
              />
              <ActionIcon
                icon="chevron-right"
                border={`1px solid ${config.transparent}`}
                fill={config.secondary}
                hoverFill={config.silver}
                onClick={() =>
                  selectYear(form?.year as number, form?.month as number, 1)
                }
                display={false}
              />
            </NavigationContainer>

            <CalendarContainer>
              <ServeCalendar month={form?.monthDetails} />
              <ServeCalendar month={form?.monthPlusDetails} />
            </CalendarContainer>
          </PickerWrapper>
        )}
      </RangeDatePickerContainer>
    </div>
  );
};
